import { create } from 'zustand'
import { cachedShop, pullCatalog, setToken, type ShopSummary } from './api'
import { kvGet, kvSet, wipeLocal } from './db'
import { applySkin, readSkin, type Skin } from './skin'

interface Session {
  token: string
  userId: string
  name: string
  role: string
  expiresAt: string
}

/**
 * Vai trò đọc từ phiên, nhưng đây chỉ là để <b>vẽ giao diện</b> cho gọn.
 * Việc chặn thật nằm ở máy chủ: sửa cờ này trong trình duyệt chỉ làm hiện thêm nút,
 * bấm vào vẫn 403.
 */
export const isOwner = (role: string | undefined) => role === 'Owner' || role === 'SysAdmin'

interface AppState {
  ready: boolean
  session: Session | null
  shop: ShopSummary | null
  toast: string | null
  /** Không mở được kho dữ liệu trên máy — app vẫn chạy nhưng không bán offline được. */
  storageBroken: boolean
  /** Kiểu giao diện của riêng máy này. Không đồng bộ lên máy chủ. */
  skin: Skin

  boot: () => Promise<void>
  signIn: (session: Session, shop: ShopSummary) => Promise<void>
  signOut: () => Promise<void>
  refreshCatalog: () => Promise<void>
  setSkin: (skin: Skin) => void
  say: (message: string) => void
}

let toastTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Kho dữ liệu trên máy có thể không trả lời — không phải ném lỗi, mà là im lặng
 * vĩnh viễn: trình duyệt đang chặn quyền lưu trữ thì `indexedDB.open` không bắn ra
 * sự kiện nào cả. Một máy bán hàng treo ở màn hình trắng thì người bán không biết
 * phải làm gì, nên mọi thao tác lúc khởi động đều có hạn chờ.
 */
function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    work,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Kho dữ liệu trên máy không trả lời')), ms),
    ),
  ])
}

export const useApp = create<AppState>((set, get) => ({
  ready: false,
  session: null,
  shop: null,
  toast: null,
  storageBroken: false,
  skin: readSkin(),

  /**
   * Mở app: lấy phiên và danh mục từ máy trước rồi vẽ ngay, sau đó mới hỏi máy chủ.
   * Người bán mở app ra là bán được, không phải đứng chờ vòng xoay tải dữ liệu.
   *
   * `ready` phải được bật trong mọi trường hợp. Kho dữ liệu trên máy có thể không mở
   * được — cửa sổ ẩn danh, máy bị khoá quyền lưu trữ, ổ đầy — và một máy bán hàng
   * đứng ở màn hình trắng thì không ai biết phải làm gì. Thà vào được màn hình đăng
   * nhập kèm lời cảnh báo còn hơn.
   */
  boot: async () => {
    try {
      const session = await withTimeout(kvGet<Session>('session'), 3000)
      const shop = await withTimeout(cachedShop(), 3000)

      if (session && new Date(session.expiresAt) > new Date()) {
        setToken(session.token)
        set({ session, shop: shop ?? null })
        void get().refreshCatalog()
      }
    } catch (err) {
      console.error('Không mở được kho dữ liệu trên máy', err)
      set({ storageBroken: true })
    } finally {
      set({ ready: true })
    }
  },

  signIn: async (session, shop) => {
    await kvSet('session', session)
    await kvSet('shop', shop)
    setToken(session.token)
    set({ session, shop })
    await get().refreshCatalog()
  },

  signOut: async () => {
    await wipeLocal()
    setToken(null)
    set({ session: null, shop: null })
  },

  setSkin: (skin) => {
    applySkin(skin)
    set({ skin })
  },

  refreshCatalog: async () => {
    try {
      const shop = await pullCatalog()
      set({ shop })
    } catch {
      // Mất mạng thì dùng danh mục đã lưu trên máy. Không báo lỗi — không có gì hỏng.
    }
  },

  say: (message) => {
    if (toastTimer) clearTimeout(toastTimer)
    set({ toast: message })
    toastTimer = setTimeout(() => set({ toast: null }), 2400)
  },
}))
